import * as yup from 'yup';

import { ValueOf } from '../@types';
import {
  ANALYSIS_TYPES,
  MARKUP_PERCENT_TYPES,
  MARKUP_TYPES,
  PULL_SEND_REQUEST_TYPES,
  RATE_TYPES,
  REQUEST_TYPES,
  TIER_VALUES,
} from './constants';
import { validators } from './utilities';

const isBatchType = (pullSendRequestType: string) =>
  [
    PULL_SEND_REQUEST_TYPES.BATCH_RATE,
    PULL_SEND_REQUEST_TYPES.AP_TESTING,
    PULL_SEND_REQUEST_TYPES.RATE_COMPARE,
    PULL_SEND_REQUEST_TYPES.GEO_MILEAGE,
  ].includes(pullSendRequestType as any);

export const pullAndSendFieldSchema = {
  fileName: yup.string().required('Results file name is required'),
  pullSendRequestType: yup
    .mixed<ValueOf<typeof PULL_SEND_REQUEST_TYPES>>()
    .oneOf(Object.values(PULL_SEND_REQUEST_TYPES))
    .required('Request Type is required'),
  dateRange: yup.object({
    startDate: yup.date().required('Start date is required').nullable(),
    endDate: yup.date().required('End date is required').nullable(),
  }),
  accounts: yup
    .array()
    .of(
      yup.object({
        name: yup.string().required(),
        enterpriseID: yup.number().required(),
        accountNumber: yup.string().required(),
      })
    )
    .min(1, 'At least one account is required')
    .required('At least one account is required'),

  // Batch-type fields (Batch Rate, AP Testing, Rate Compare, GEO Mileage)
  returnCustomerRates: yup.boolean(),
  returnLiveContractRates: yup.boolean(),
  returnProposalContractRates: yup.boolean(),
  returnVolumeRates: yup.boolean(),
  targetedCarriers: validators.carrierList,

  // Rating Analysis fields
  requestType: yup.mixed().when('pullSendRequestType', {
    is: PULL_SEND_REQUEST_TYPES.RATING_ANALYSIS,
    then: yup
      .mixed<ValueOf<typeof REQUEST_TYPES>>()
      .oneOf(Object.values(REQUEST_TYPES))
      .required('Request Type is required'),
  }),
  rateType: yup.mixed().when('pullSendRequestType', {
    is: PULL_SEND_REQUEST_TYPES.RATING_ANALYSIS,
    then: yup
      .mixed<ValueOf<typeof RATE_TYPES>>()
      .oneOf(Object.values(RATE_TYPES))
      .required('Rate Type is required'),
  }),
  carrierTiers: yup.mixed().when('pullSendRequestType', {
    is: PULL_SEND_REQUEST_TYPES.RATING_ANALYSIS,
    then: yup
      .array()
      .min(1, 'Carrier Tiers is required')
      .of(yup.mixed<TIER_VALUES>().required('Carrier Tiers is required'))
      .required('Carrier Tiers is required'),
  }),
  carrierExclusions: validators.carrierList,
  carrierInclusions: validators.carrierList,
  analysisType: yup.mixed().when('pullSendRequestType', {
    is: PULL_SEND_REQUEST_TYPES.RATING_ANALYSIS,
    then: yup
      .mixed<ValueOf<typeof ANALYSIS_TYPES>>()
      .oneOf(Object.values(ANALYSIS_TYPES))
      .required('Analysis Type is required'),
  }),
  markupType: yup.mixed().when(['pullSendRequestType', 'rateType'], {
    is: (pullSendRequestType: string, rateType: string) =>
      pullSendRequestType === PULL_SEND_REQUEST_TYPES.RATING_ANALYSIS &&
      rateType === RATE_TYPES.CARRIER,
    then: yup
      .mixed<ValueOf<typeof MARKUP_TYPES>>()
      .oneOf(Object.values(MARKUP_TYPES))
      .required('Markup Type is required'),
  }),
  markupPercentType: yup.mixed().when('markupType', {
    is: MARKUP_TYPES.PERCENT,
    then: yup
      .mixed<ValueOf<typeof MARKUP_PERCENT_TYPES>>()
      .oneOf(Object.values(MARKUP_PERCENT_TYPES))
      .required('Markup Percentage Type is required'),
  }),
  markupAmount: yup.mixed().when(['pullSendRequestType', 'rateType', 'markupType'], {
    is: (pullSendRequestType: string, rateType: string, markupType: string) =>
      pullSendRequestType === PULL_SEND_REQUEST_TYPES.RATING_ANALYSIS &&
      rateType === RATE_TYPES.CARRIER &&
      markupType !== MARKUP_TYPES.CURRENT,
    then: yup.lazy((value) =>
      value === ''
        ? yup.string().required('Markup value is required')
        : yup.number().required('Markup value is required')
    ),
  }),
  impactScacRestrictions: validators.carrierList,
  originZipExclusions: validators.zipCode,
  destinationZipExclusions: validators.zipCode,
  originStateExclusions: yup.array().of(validators.state),
  destinationStateExclusions: yup.array().of(validators.state),
  includeP44Dynamic: yup.boolean(),
  includeP44Volume: yup.boolean(),
  notifyEmails: yup
    .array(
      yup
        .object({
          value: validators.blugraceEmail.required(),
        })
        .required()
    )
    .required(),
};
